import {
  CheckPhoneNumberUpdateResponse,
  useToast,
  LogoOutline as Logo,
} from '@aslanpay/portal-common-lib';
import {
  Box,
  Button,
  Circle,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Text,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';

import PhoneNumberInput from 'app/components/UI/Inputs/PhoneNumberInput';
import useCheckPhoneNumberUpdate from 'app/hooks/useCheckPhoneNumberUpdate';
import { authenticationSchema } from 'app/utils/schemas';
import { Controller, useForm } from 'react-hook-form';
import { usePhoneVerificationStore } from 'app/store';

import { InputPhoneStepProps, PhoneVerificationFormFields } from './types';

function InputPhoneStep({
  setStep,
  setPhoneNumber,
  setTransactionId,
  onClose,
}: InputPhoneStepProps) {
  const toast = useToast();

  const { showWelcomeHeader } = usePhoneVerificationStore();

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<PhoneVerificationFormFields>({
    mode: 'onChange',
    resolver: yupResolver(authenticationSchema.enableTwoFactor),
    defaultValues: {
      phoneNumber: '',
    },
  });

  const { mutate: checkPhoneNumber } = useCheckPhoneNumberUpdate({
    onSuccess: (data: CheckPhoneNumberUpdateResponse) => {
      setTransactionId(data.transactionId);
      setStep('VERIFY_PHONE');
    },
    onError: () => {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: 'Sorry, it was not possible to check your phone number. Please try again.',
      });
    },
  });

  const handleSendCode = (data: PhoneVerificationFormFields) => {
    const phoneNumber = data.phoneNumber.split(' ').join('');
    checkPhoneNumber(phoneNumber);
    setPhoneNumber(phoneNumber);
  };

  return (
    <Flex position="relative" flexDirection="column" gap="40px">
      {showWelcomeHeader && (
        <>
          <Circle position="absolute" top="16px" left="16px" bg="brand.white" p="16px">
            <Logo color="brand.purple" boxSize="40px" />
          </Circle>
          <Text
            w="full"
            px="103px"
            pb="18px"
            pt="22px"
            align="center"
            justifyContent="center"
            textStyle="h4SemiBold"
            color="brand.white"
            bgGradient="linear(to-r, brand.darkPurple, brand.grayishBlack)"
          >
            Welcome to the Gifting Portal
          </Text>
        </>
      )}

      <Box px="24px" pb="24px" pt={showWelcomeHeader ? '0px' : '24px'}>
        <form onSubmit={handleSubmit(handleSendCode)} noValidate>
          <Text textStyle="h5Bold" color="brand.black" mb="10px">
            Set up two-factor authentication
          </Text>
          <Text textStyle="body" color="brand.mutedPurple">
            Let&apos;s make your account secure.
          </Text>

          <FormControl isInvalid={!!errors.phoneNumber?.message} isRequired my="20px">
            <FormLabel color="brand.mutedPurple" fontSize="14px" mb="12px">
              Mobile number
            </FormLabel>
            <Controller
              control={control}
              name="phoneNumber"
              render={({ field: { onChange, onBlur, value } }) => (
                <PhoneNumberInput
                  defaultValue={value}
                  placeholder="Mobile number"
                  onChange={onChange}
                  onBlur={onBlur}
                />
              )}
            />
            <FormErrorMessage>{errors.phoneNumber?.message}</FormErrorMessage>
          </FormControl>

          <Flex justify="space-between" align="center">
            <Text textStyle="extraSmallMedium" color="system.error">
              * mandatory
            </Text>

            <Flex gap="10px" align="center">
              <Button variant="ghost" onClick={onClose}>
                Skip
              </Button>
              <Button variant="solid" type="submit" isDisabled={!isValid}>
                Send code
              </Button>
            </Flex>
          </Flex>
        </form>
      </Box>
    </Flex>
  );
}

export default InputPhoneStep;
